import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, ChevronDown, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type FieldValues, type Resolver, type SubmitHandler, type UseFormRegister, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { z } from "zod";
import { isLocale, localizedPath } from "../../lib/routes";
import { track } from "../../lib/analytics";
import { submitLead } from "../../services/leadService";
import { Button } from "../ui/Button";

const schema = z.object({
  challenges: z.array(z.string()).min(1, "Select at least one"),
  message: z.string().trim().max(1800).optional(),
  sector: z.string().min(1, "Sector is required"),
  location: z.string().trim().max(120).optional(),
  existingAssets: z.array(z.string()).default([]),
  existingPvCapacity: z.string().trim().max(80).optional(),
  annualEnergyCost: z.string().trim().max(80).optional(),
  outages: z.string().min(1, "Select an option"),
  objectives: z.array(z.string()).min(1, "Select at least one"),
  objectivesOther: z.string().trim().max(200).optional(),
  investmentHorizon: z.string().min(1, "Select an option"),
  existingStudies: z.string().min(1, "Select an option"),
  financingExplored: z.string().min(1, "Select an option"),
  financingDetails: z.string().trim().max(600).optional(),
  organization: z.string().trim().min(2, "Organization is required").max(160),
  contactName: z.string().trim().min(2, "Your name is required").max(120),
  email: z.string().trim().email("Valid email is required").max(160),
  role: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(80).optional(),
  website: z.string().trim().max(160).optional(),
  consent: z.boolean().refine(Boolean, "Consent is required"),
  honeypot: z.string().max(0).optional()
});

const steps = ["The Challenge", "The Site", "Readiness", "About You"];
const stepFields: string[][] = [
  ["challenges"],
  ["sector", "outages"],
  ["objectives", "investmentHorizon", "existingStudies", "financingExplored"],
  ["organization", "contactName", "email"]
];

const challengeOptions = ["Energy cost", "Reliability / outages", "Water / irrigation", "Climate pressure", "Modernization", "Capacity growth", "Sustainability requirements", "Other", "Not sure"];
const sectors = ["Winery", "Food processing", "Cold storage", "Horticulture / orchard", "Greenhouse", "Irrigation", "Agriculture", "Manufacturing", "Municipality / community", "Other"];
const assetOptions = ["Solar PV", "Generator", "Battery storage", "Refrigeration / cooling", "Irrigation / pumps", "Major process equipment", "Other", "Not sure"];
const outagesOptions = ["Frequently", "Sometimes", "Rarely", "No", "Not sure"];
const objectiveOptions = ["Reduce operating cost", "Improve resilience", "Modernize equipment or infrastructure", "Increase capacity", "Improve water/resource efficiency", "Meet sustainability / market requirements", "Explore a new investment", "Other"];
const horizonOptions = ["Actively planning now", "Within 6 months", "Within 6–12 months", "More than 12 months", "Exploring options", "Not sure"];
const yesNoNotSure = ["Yes", "No", "Not sure"];

function LabelText({ label, required }: { label: string; required?: boolean }) {
  return <span>{label}{required ? <span className="text-brand-error"> *</span> : <span className="font-normal text-ink-secondary"> (Optional)</span>}</span>;
}

function TextField({ label, name, register, error, required, type = "text", helper }: { label: string; name: string; register: UseFormRegister<FieldValues>; error?: string; required?: boolean; type?: string; helper?: string }) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      <LabelText label={label} required={required} />
      <input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3.5 py-2.5 text-[0.95rem] transition-colors hover:border-brand-blue/40 focus:border-brand-blue" type={type} {...register(name)} aria-invalid={Boolean(error)} />
      {helper && !error && <span className="text-xs font-normal leading-5 text-ink-secondary">{helper}</span>}
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

function TextArea({ label, name, register, error, required, helper }: { label: string; name: string; register: UseFormRegister<FieldValues>; error?: string; required?: boolean; helper?: string }) {
  return (
    <label className="grid gap-2 text-sm font-semibold md:col-span-2">
      <LabelText label={label} required={required} />
      <textarea className="min-h-28 w-full rounded-xl border border-line bg-surface px-3.5 py-2.5 text-[0.95rem] leading-7 transition-colors hover:border-brand-blue/40 focus:border-brand-blue" {...register(name)} aria-invalid={Boolean(error)} />
      {helper && !error && <span className="text-xs font-normal leading-5 text-ink-secondary">{helper}</span>}
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

function SelectField({ label, name, options, register, error, required, helper }: { label: string; name: string; options: string[]; register: UseFormRegister<FieldValues>; error?: string; required?: boolean; helper?: string }) {
  return (
    <label className="grid gap-2 text-sm font-semibold">
      <LabelText label={label} required={required} />
      <span className="relative block">
        <select className="min-h-11 w-full appearance-none rounded-lg border border-line bg-surface px-3.5 py-2.5 pr-10 text-[0.95rem] transition-colors hover:border-brand-blue/40 focus:border-brand-blue" {...register(name)} aria-invalid={Boolean(error)}>
          <option value=""></option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-secondary" aria-hidden="true" />
      </span>
      {helper && !error && <span className="text-xs font-normal leading-5 text-ink-secondary">{helper}</span>}
      {error && <span className="text-sm font-medium text-brand-error">{error}</span>}
    </label>
  );
}

function CheckboxGroup({ label, name, options, register, error, helper, columns = 2 }: { label: string; name: string; options: string[]; register: UseFormRegister<FieldValues>; error?: string; helper?: string; columns?: number }) {
  return (
    <fieldset className="md:col-span-2">
      <legend className="mb-3 text-sm font-semibold"><LabelText label={label} required /></legend>
      {helper && <p className="mb-3 text-xs leading-5 text-ink-secondary">{helper}</p>}
      <div className={`grid gap-3 ${columns === 2 ? "sm:grid-cols-2" : ""}`}>
        {options.map((item) => (
          <label key={item} className="flex min-h-11 items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-semibold">
            <input type="checkbox" value={item} {...register(name)} />
            {item}
          </label>
        ))}
      </div>
      {error && <p className="mt-3 text-sm font-medium text-brand-error">{error}</p>}
    </fieldset>
  );
}

export function ScreeningWizard() {
  const { locale } = useParams();
  const location = useLocation();
  const activeLocale = isLocale(locale) ? locale : "en";
  const [step, setStep] = useState(0);
  const [startedAt] = useState(Date.now());
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const storageKey = "ptd:moldova:screening";

  const form = useForm<FieldValues>({
    resolver: zodResolver(schema) as unknown as Resolver<FieldValues>,
    mode: "onBlur",
    defaultValues: useMemo(() => { try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch { return {}; } }, [storageKey])
  });
  const register = form.register;

  useEffect(() => {
    const sub = form.watch((value) => localStorage.setItem(storageKey, JSON.stringify({ ...value, consent: false })));
    return () => sub.unsubscribe();
  }, [form, storageKey]);

  useEffect(() => { track("moldova_screening_started", { locale: activeLocale }); }, [activeLocale]);

  const onSubmit: SubmitHandler<FieldValues> = async (values) => {
    setStatus("submitting");
    const assets: string[] = values.existingAssets ?? [];
    const data = {
      ...values,
      existingPv: assets.includes("Solar PV") ? "Yes" : assets.includes("Not sure") ? "Not sure" : "No",
      existingStorage: assets.includes("Battery storage") || assets.includes("Generator") ? "Yes" : assets.includes("Not sure") ? "Not sure" : "No",
      objectives: values.objectives ?? []
    };
    try {
      await submitLead({ leadType: "moldova_project", locale: activeLocale, sourcePage: location.pathname, consent: Boolean(values.consent), startedAt, honeypot: values.honeypot, data });
      track("moldova_screening_completed", { locale: activeLocale });
      localStorage.removeItem(storageKey);
      form.reset();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  function clearDraft() {
    localStorage.removeItem(storageKey);
    form.reset({});
    setStep(0);
  }

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-line bg-surface p-8 shadow-soft md:p-10">
        <p className="eyebrow mb-4">SCREENING RECEIVED</p>
        <h2 className="text-balance text-2xl font-bold leading-tight md:text-3xl">Thank you. We’ll review the case before suggesting a next step.</h2>
        <p className="mt-5 max-w-2xl leading-7 text-ink-secondary">The purpose of the screening is to determine whether there is enough evidence to justify further work. If clarification is needed, we may contact you for additional information or a short discussion.</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button to={localizedPath(activeLocale, "moldova")}>Back to Moldova</Button>
          <Button variant="secondary" to={localizedPath(activeLocale, "programs")}>Explore Programs</Button>
        </div>
      </div>
    );
  }

  const errors = form.formState.errors as Record<string, { message?: string }>;
  const assets = (form.watch("existingAssets") as string[] | undefined) ?? [];
  const objectives = (form.watch("objectives") as string[] | undefined) ?? [];
  const financingExplored = form.watch("financingExplored") as string | undefined;

  return (
    <form className="rounded-2xl border border-line bg-surface p-4 shadow-soft md:p-8" onSubmit={form.handleSubmit(onSubmit)} noValidate>
      <div className="mb-7">
        <div className="mb-3 flex items-center justify-between gap-3 text-sm font-semibold">
          <span>{steps[step]}</span>
          <span>{step + 1} / {steps.length}</span>
        </div>
        <div className="h-1.5 rounded-full bg-line/80" role="progressbar" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={step + 1} aria-label={`Step ${step + 1} of ${steps.length}`}>
          <div className="h-1.5 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan transition-all duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 text-sm text-ink-secondary">
          <span>Draft saved on this device.</span>
          <button type="button" className="font-semibold text-brand-blue underline-offset-2 hover:underline" onClick={clearDraft}>Clear draft</button>
        </div>
      </div>

      <input className="hidden" tabIndex={-1} autoComplete="off" {...form.register("honeypot")} aria-label="Leave this field empty." />

      <div className="grid gap-5 md:grid-cols-2">
        {step === 0 && (
          <>
            <CheckboxGroup label="What is putting pressure on your operation?" name="challenges" options={challengeOptions} register={register} error={errors.challenges?.message} helper="Choose everything that is relevant. You can refine it later." />
            <TextArea label="Describe the problem in your own words" name="message" register={register} error={errors.message?.message} helper="A few sentences are enough." />
          </>
        )}

        {step === 1 && (
          <>
            <SelectField label="What kind of operation is this?" name="sector" options={sectors} register={register} error={errors.sector?.message} required />
            <TextField label="City / district" name="location" register={register} error={errors.location?.message} />
            <CheckboxGroup label="What existing assets are on site?" name="existingAssets" options={assetOptions} register={register} error={errors.existingAssets?.message} />
            {assets.includes("Solar PV") && <TextField label="Approximate installed solar capacity" name="existingPvCapacity" register={register} error={errors.existingPvCapacity?.message} />}
            <TextField label="Approximate annual energy cost" name="annualEnergyCost" register={register} error={errors.annualEnergyCost?.message} helper="An approximate figure is enough." />
            <SelectField label="Do outages or grid constraints affect operations?" name="outages" options={outagesOptions} register={register} error={errors.outages?.message} required />
          </>
        )}

        {step === 2 && (
          <>
            <CheckboxGroup label="What are you trying to achieve?" name="objectives" options={objectiveOptions} register={register} error={errors.objectives?.message} />
            {objectives.includes("Other") && <TextField label="Other objective" name="objectivesOther" register={register} error={errors.objectivesOther?.message} />}
            <SelectField label="Investment horizon" name="investmentHorizon" options={horizonOptions} register={register} error={errors.investmentHorizon?.message} required />
            <SelectField label="Do you already have technical studies, audits or project proposals?" name="existingStudies" options={yesNoNotSure} register={register} error={errors.existingStudies?.message} required />
            <SelectField label="Have you already explored financing or support programmes?" name="financingExplored" options={yesNoNotSure} register={register} error={errors.financingExplored?.message} required />
            {financingExplored === "Yes" && <TextArea label="What have you explored?" name="financingDetails" register={register} error={errors.financingDetails?.message} />}
          </>
        )}

        {step === 3 && (
          <>
            <TextField label="Organization name" name="organization" register={register} error={errors.organization?.message} required />
            <TextField label="Contact name" name="contactName" register={register} error={errors.contactName?.message} required />
            <TextField label="Email" name="email" type="email" register={register} error={errors.email?.message} required />
            <TextField label="Role" name="role" register={register} error={errors.role?.message} />
            <TextField label="Phone" name="phone" register={register} error={errors.phone?.message} />
            <TextField label="Website" name="website" register={register} error={errors.website?.message} />
            <div className="rounded-xl border border-line bg-bg-secondary p-4 md:col-span-2">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-secondary">Summary</p>
              <p className="mt-2 text-sm leading-6 text-ink-secondary">
                {(form.watch("challenges") as string[] | undefined)?.join(", ") || "No challenges selected"}
                {form.watch("sector") ? ` · ${form.watch("sector")}` : ""}
              </p>
            </div>
            <label className="flex items-start gap-3 rounded-xl border border-line bg-surface p-4 text-sm font-semibold md:col-span-2">
              <input className="mt-1" type="checkbox" {...form.register("consent")} />
              <span>I agree that TransitionBridge may use the information submitted here to review this screening and contact me about potential next steps. <a className="text-brand-blue underline" href={`/${activeLocale}/privacy`}>Privacy Policy</a></span>
            </label>
            {errors.consent?.message && <p className="text-sm font-medium text-brand-error md:col-span-2">{errors.consent.message}</p>}
            <div className="flex items-center gap-2 text-sm text-ink-secondary md:col-span-2">
              <ShieldCheck className="h-4 w-4 text-brand-success" aria-hidden="true" />
              <span>Your information is used only to review this screening.</span>
            </div>
          </>
        )}
      </div>

      {status === "error" && <p className="mt-5 rounded-lg border border-brand-error/30 bg-white p-3 text-sm font-medium text-brand-error">We could not submit right now. Your draft is kept on this device — please review the fields or try again.</p>}

      <div className={`mt-8 flex flex-col gap-3 sm:flex-row ${step > 0 ? "sm:justify-between" : "sm:justify-end"}`}>
        {step > 0 && (
          <Button variant="secondary" arrow={false} disabled={status === "submitting"} onClick={() => setStep((v) => Math.max(0, v - 1))}>Back</Button>
        )}
        {step < steps.length - 1 ? (
          <Button type="button" onClick={async () => {
            const ok = await form.trigger(stepFields[step]);
            if (ok) setStep((v) => Math.min(steps.length - 1, v + 1));
          }}>Continue</Button>
        ) : (
          <Button type="submit" className="sm:min-w-64">{status === "submitting" ? "Submitting..." : "Submit Project Screening"}</Button>
        )}
      </div>
    </form>
  );
}
